import { type ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { nextFrameHeight } from './frameHeight';
import styles from './SafeMessageBody.module.css';
import { applyLinkRule, removeAnimationElements, removeShadowHosts, safeSrcDoc } from './safeSrcDoc';

export interface SafeMessageBodyProps {
    /** The stored body, or `null` when the message has none. Untrusted in either content type. */
    readonly body: string | null;
    readonly contentType: 'HTML' | 'TEXT';
    /** The frame's accessible name. */
    readonly title?: string;
    readonly 'data-testid'?: string;
}

/** The custom property the measured height is written to, as a bare number (`* 1px` in the stylesheet). */
const FRAME_BLOCK_PROPERTY = '--et-message-body-block';

/** The frame's sandbox, exactly: no `allow-scripts`, `allow-forms`, `allow-top-navigation` or `allow-modals`. */
export const MESSAGE_FRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';

/**
 * The CRM's text style for the frame's document, read from the container at run time: custom
 * properties do not cross into the frame, and reading computed values keeps every literal out of this
 * source (F20-c WI-1.3 plan Decision 12). The email's own styles follow it and may override it.
 */
function frameStyleFrom(element: HTMLElement): string {
    const computed = window.getComputedStyle(element);
    const declarations = (
        [
            ['font-family', computed.fontFamily],
            ['font-size', computed.fontSize],
            ['line-height', computed.lineHeight],
            ['color', computed.color],
        ] as const
    )
        .filter(([, value]) => value !== '')
        .map(([property, value]) => `${property}: ${value};`)
        .join(' ');
    return `body { margin: 0; overflow-wrap: anywhere; ${declarations} } img { max-inline-size: 100%; }`;
}

/**
 * The reviewed way to show an untrusted email body (F20-c spec FR-I3, NFR-1; F20-c WI-1.3 plan
 * Decisions 9–12; design-system §5 *Safe message body*). React's raw-HTML prop stays banned.
 *
 *  - `TEXT` — a React text child (escaped) in a `white-space: pre-wrap` block: markup shows as text,
 *    line breaks are kept. Never put in the frame.
 *  - `HTML` — an `<iframe srcdoc>` sandboxed with `allow-same-origin allow-popups
 *    allow-popups-to-escape-sandbox` only: no script or inline handler runs (no `allow-scripts`), no
 *    form submits (no `allow-forms`), nothing navigates the CRM (no `allow-top-navigation`).
 *    `allow-same-origin` without `allow-scripts` lets this component measure and re-check the frame's
 *    document; nothing in the frame can use it. The document is built by `safeSrcDoc`: its policy;
 *    the link rule; the removal of `script`, `iframe`, `frame`, `object`, `embed`, `link`, `base`, the
 *    email's own `meta`, the SVG `set`, `animate` and `animateTransform`, and every `template` (so no
 *    declarative shadow root) and `shadowroot…` attribute; and a second parse of the result, which
 *    falls back to the body's plain text when anything removed reappears. On the loaded document the
 *    link rule is applied once more, and any element hosting a shadow root and any SVG animation
 *    element are removed.
 *  - `null` — *This message has no text*.
 *
 * The frame's height follows its content (`nextFrameHeight`, with a limit on growth); where the
 * frame's document cannot be read or `ResizeObserver` is missing (jsdom) it keeps its minimum height
 * and scrolls inside. Markup wider than the column scrolls inside the frame, never the page.
 *
 * Renders no control of its own (`inert` in the interaction-state manifest): links inside the frame
 * belong to the email.
 */
export function SafeMessageBody({
    body,
    contentType,
    title = 'Message body',
    'data-testid': testId,
}: SafeMessageBodyProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLIFrameElement>(null);
    const observerRef = useRef<ResizeObserver | null>(null);
    const [frameStyle, setFrameStyle] = useState<string | null>(null);

    const isFramed = contentType === 'HTML' && body !== null;

    // Read before paint, so the frame is rendered once, with its document, and never as a blank page.
    useLayoutEffect(() => {
        if (isFramed && rootRef.current !== null) {
            setFrameStyle(frameStyleFrom(rootRef.current));
        }
    }, [isFramed]);

    const srcDoc = useMemo(
        () => (isFramed && frameStyle !== null ? safeSrcDoc(body, frameStyle) : null),
        [isFramed, body, frameStyle],
    );

    const handleLoad = useCallback(() => {
        observerRef.current?.disconnect();
        observerRef.current = null;
        const frame = frameRef.current;
        const doc = frame?.contentDocument ?? null;
        if (frame === null || doc === null) {
            return;
        }
        // The shadow-host removal, the link rule and the animation removal once more, on the document
        // the browser built: what a reader clicks is this one, not the string `safeSrcDoc` returned.
        removeShadowHosts(doc);
        applyLinkRule(doc);
        removeAnimationElements(doc);
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        let written = 0;
        let steps = 0;
        let stopped = false;
        const measure = () => {
            if (stopped) {
                return;
            }
            // The root's own height, not `scrollHeight`, which never drops below the frame's height and
            // so would never let a frame shrink.
            const measured = Math.ceil(doc.documentElement.getBoundingClientRect().height);
            const next = nextFrameHeight(written, measured, steps);
            steps = next.growthSteps;
            if (next.write !== null) {
                written = next.write;
                frame.style.setProperty(FRAME_BLOCK_PROPERTY, String(next.write));
            }
            if (next.stop) {
                stopped = true;
                observerRef.current?.disconnect();
            }
        };
        // Once at load, so the frame fits its content even if the observer below never reports: it
        // belongs to this page and watches an element of the frame's document.
        measure();
        if (stopped) {
            return;
        }
        const observer = new ResizeObserver(measure);
        observerRef.current = observer;
        observer.observe(doc.documentElement);
    }, []);

    // Attached here rather than as an `onLoad` prop (Biome's a11y rules read a handler on a frame as
    // an interaction), and in a layout effect, so it is in place before the frame's document can load.
    useLayoutEffect(() => {
        const frame = frameRef.current;
        if (srcDoc === null || frame === null) {
            return;
        }
        frame.addEventListener('load', handleLoad);
        return () => {
            frame.removeEventListener('load', handleLoad);
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, [srcDoc, handleLoad]);

    let content: ReactNode;
    if (body === null) {
        content = <p className={styles.empty}>This message has no text</p>;
    } else if (contentType === 'TEXT') {
        content = <div className={styles.text}>{body}</div>;
    } else {
        content =
            srcDoc === null ? null : (
                <iframe
                    className={styles.frame}
                    ref={frameRef}
                    referrerPolicy="no-referrer"
                    sandbox={MESSAGE_FRAME_SANDBOX}
                    srcDoc={srcDoc}
                    title={title}
                />
            );
    }

    return (
        <div className={styles.root} data-testid={testId} ref={rootRef}>
            {content}
        </div>
    );
}
